import { PayloadAction, createSlice } from "@reduxjs/toolkit";

import {
  IMarketer,
  IMarketerCreateData,
  IMarketerDetails,
  IMarketersResponse,
  IMarketerState,
} from "../types/marketers";

const initialState: IMarketerState = {
  marketers: [],
  marketer: null,
  total: 0,
  limit: 10,
  offset: 0,
  loading: false,
  loadingMarketerId: null,
};

export const marketer = createSlice({
  name: "marketer",
  initialState,

  reducers: {
    // Set marketers returned from API
    setMarketers: (state, action: PayloadAction<IMarketersResponse>) => {
      state.marketers = action.payload.data.items;
      state.total = action.payload.data.total;
      state.limit = action.payload.data.limit;
      state.offset = action.payload.data.offset;
    },

    // Set a single marketer
    setMarketer: (state, action: PayloadAction<IMarketerDetails>) => {
      state.marketer = action.payload;
    },

    // Add a new marketer
    createMarketer: (state, action: PayloadAction<IMarketerCreateData>) => {
      if (state.offset === 0) {
        state.marketers = [
          action.payload,
          ...state.marketers.slice(0, Math.max(0, state.limit - 1)),
        ];
      }
      state.total += 1;
    },

    // Update a marketer
    editMarketer: (state, action: PayloadAction<IMarketer>) => {
      state.marketers = state.marketers.map((item) =>
        item.id === action.payload.id ? action.payload : item,
      );
      if (state.marketer?.id === action.payload.id) {
        state.marketer = { ...state.marketer, ...action.payload };
      }
    },

    updateMarketerStatus: (
      state,
      action: PayloadAction<{ id: number; is_active: boolean }>,
    ) => {
      state.marketers = state.marketers.map((item) =>
        item.id === action.payload.id
          ? { ...item, is_active: action.payload.is_active }
          : item,
      );
      if (state.marketer?.id === action.payload.id) {
        state.marketer.is_active = action.payload.is_active;
      }
    },

    // Delete a marketer
    deleteMarketer: (state, action: PayloadAction<number>) => {
      state.marketers = state.marketers.filter(
        (item) => item.id !== action.payload,
      );
    },

    // Pagination
    setLimit: (state, action: PayloadAction<number>) => {
      state.limit = action.payload;
    },

    setOffset: (state, action: PayloadAction<number>) => {
      state.offset = action.payload;
    },

    // Loading
    setMarketerLoading: (state) => {
      state.loading = true;
    },

    disableMarketerLoading: (state) => {
      state.loading = false;
    },

    // Loading a specific marketer
    setLoadingMarketerId: (state, action: PayloadAction<number | null>) => {
      state.loadingMarketerId = action.payload;
    },

    // Clear selected marketer
    clearMarketer: (state) => {
      state.marketer = null;
    },
  },
});

export const {
  setMarketers,
  setMarketer,
  createMarketer,
  editMarketer,
  updateMarketerStatus,
  deleteMarketer,
  setLimit,
  setOffset,
  setMarketerLoading,
  disableMarketerLoading,
  setLoadingMarketerId,
  clearMarketer,
} = marketer.actions;

export default marketer.reducer;
